/**
 * In the iOS app, pages slide in from the right as they open from another
 * page, and back out to the right going back, as iOS's navigation does: the
 * app slides its whole screen (header and tab bar too) natively, and lets the
 * user swipe back from the left edge. Between the tabs' own pages it cuts, as
 * tab bars do. Elsewhere (the website, its PWA) pages change as they always
 * have, but the history of the visit still names the page to go back to.
 */

import type { AppRouterLike } from "AppRouter";
import * as iOSBridge from "iOSBridge";

/** The tabs' own pages (the app's tabs, and the website's), which the others open from */
const tabPages = new Set([
  "dashboardPage",
  "libraryPage",
  "discoverPage",
  "wordsPage",
  "learnPage",
]);

/**
 * The tab each page belongs to, by page name: a tab's own pages, and the
 * pages opened from them (Story Bot and Upload from the Library, the
 * websites from Discover…). The website's tabs are highlighted in
 * _header.scss.
 */
const tabByPage: Record<string, string> = {
  dashboardPage: "Home",
  progressPage: "Home",
  libraryPage: "Library",
  storyBot: "Library",
  uploadPage: "Library",
  audioUploadedPage: "Library",
  discoverPage: "Discover",
  websitesPage: "Discover",
  publicShelfPage: "Discover",
  wordsPage: "Words",
  importWordsPage: "Words",
  learnPage: "Practice",
  flashcardsPage: "Practice",
  chatListPage: "Practice",
};

/** Each tab's own page (My Texts at the shelf or tag last shown) */
export const tabPaths: Record<string, string> = {
  Home: "/dashboard",
  Library: "/myTexts",
  Discover: "/library",
  Words: "/words",
  Practice: "/learn",
};

/** The tab a page belongs to */
export const tabOf = (pageName: string): string | undefined =>
  tabByPage[pageName];

type Direction = "forward" | "back";

let shownPageName: string | undefined;
/** The next page comes from going back (or forward) through the history */
let fromHistory = false;
/** …and the app has animated it already (its swipe back), covering the page as it changes */
let animatedByApp = false;
/** The next page comes from a tab */
let fromTab = false;

window.addEventListener("popstate", () => {
  fromHistory = true;
});

/** The next page is a tab's: it's shown without sliding */
export const switchTab = () => {
  fromTab = true;
};

/**
 * Each page of this visit by its place in the history, with its history
 * entry's key where the browser has the Navigation API
 */
const pagesByHistoryIndex = new Map<
  number,
  { name: string; path: string; key?: string }
>();

const historyIndex = (): number => window.history.state?.index ?? 0;

/** Notes the page at this place in the history, and its URL */
const notePage = (name: string) => {
  pagesByHistoryIndex.set(historyIndex(), {
    name,
    path: window.location.pathname,
    key: window.navigation?.currentEntry?.key,
  });
};

/**
 * Notes a URL the page shown has moved to itself (as My Texts does, picking a
 * tag), so that going back to the page returns to it there
 */
export const followURL = () => {
  if (shownPageName) {
    notePage(shownPageName);
  }
};

/**
 * The sessions (Practice's, classic flashcards, a chat, a text's review),
 * which going back skips: left or finished, they'd start again
 */
const sessionPages = new Set([
  "practicePage",
  "flashcardsPage",
  "chatPage",
  "readerReview",
]);

/**
 * Where in the history the page to go back to is: the latest before this one,
 * skipping this page's own entries (e.g. the reader's, as it turns pages,
 * which it shows itself) and sessions
 */
const previousPageIndex = () => {
  for (let index = historyIndex() - 1; index >= 0; index--) {
    const page = pagesByHistoryIndex.get(index);
    if (page && page.name !== shownPageName && !sessionPages.has(page.name)) {
      return index;
    }
  }
  return undefined;
};

/** Whether there's a page of this visit to go back to, before this one */
export const canGoBack = () => previousPageIndex() !== undefined;

/**
 * Back to the page before this one (not to this one as it was): by its entry
 * where the browser has the Navigation API. Without it, counting entries back
 * still counts those an iframe has added (as pages visited inside the
 * sidebar's dictionary do). The iOS app shows the dictionary natively, so
 * isn't affected.
 */
export const goBack = () => {
  const index = previousPageIndex();
  if (index === undefined) {
    return;
  }
  const { key } = pagesByHistoryIndex.get(index)!;
  if (key && window.navigation) {
    window.navigation.traverseTo(key);
  } else {
    window.history.go(index - historyIndex());
  }
};

/** The iOS app's swipe back, once swiped: goes back, which it's animated */
const goBackSwiped = () => {
  animatedByApp = true;
  goBack();
};

/** A page's title, as a back button names it, from its path */
const titleOf = (path: string) => {
  const titles: [RegExp, string][] = [
    [/^\/(\w+\/)?dashboard/, "Home"],
    [/^\/(\w+\/)?myTexts/, "Library"],
    [/^\/(\w+\/)?library$/, "Discover"],
    [/^\/(\w+\/)?links/, "Websites"],
    [/^\/(\w+\/)?words/, "Words"],
    [/^\/(\w+\/)?(learn|flashcards|practice)/, "Practice"],
    [/^\/shelf\//, "Shelf"],
    [/^\/user\//, "Profile"],
  ];
  return titles.find(([pattern]) => pattern.test(path))?.[1] ?? "Back";
};

/**
 * The title of the page to go back to: the one before in this visit's
 * history, or else `fallbackPath`'s
 */
export const backTitle = (fallbackPath: string) => {
  const index = previousPageIndex();
  return titleOf(
    (index !== undefined && pagesByHistoryIndex.get(index)?.path) ||
      fallbackPath
  );
};

/** Whether it's one of the tabs' own pages, which the others open from */
export const isTabPage = (pageName: string) => tabPages.has(pageName);

/**
 * Where a page opened from another goes back to, titled: the page before it
 * in this visit, or else its tab's page (none for a tab's own page, or a page
 * of no tab with none before it)
 */
export const back = (
  pageName: string
): { title: string; go: (appRouter: AppRouterLike) => void } | undefined => {
  if (isTabPage(pageName)) {
    return undefined;
  }
  const tab = tabOf(pageName);
  const tabPath = tab ? tabPaths[tab] : undefined;
  if (canGoBack()) {
    return { title: backTitle(tabPath ?? "/dashboard"), go: () => goBack() };
  }
  return tab && tabPath
    ? {
        title: tab,
        go: (appRouter) => appRouter.navigate(tabPath, { trigger: true }),
      }
    : undefined;
};

const depth = (pageName: string) => (isTabPage(pageName) ? 0 : 1);

const direction = (from: string, to: string): Direction | undefined => {
  if (from === to || fromTab) {
    return undefined;
  }
  if (depth(to) !== depth(from)) {
    return depth(to) > depth(from) ? "forward" : "back";
  }
  if (depth(to) === 0) {
    return undefined;
  }
  return fromHistory ? "back" : "forward";
};

/** Resolves once the page has been drawn, or soon if it's slow to load */
const drawn = (rendered: Promise<void>) =>
  Promise.race([
    rendered.then(
      () =>
        new Promise<void>((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
        )
    ),
    new Promise<void>((resolve) => setTimeout(resolve, 500)),
  ]);

/**
 * Shows the page named, which `update` renders: `rendered` resolves once
 * it's ready to see
 */
export const show = async (
  pageName: string,
  update: () => Promise<void>,
  rendered: Promise<void>
) => {
  const transition =
    shownPageName &&
    !animatedByApp &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? direction(shownPageName, pageName)
      : undefined;
  const appCovering = animatedByApp;
  shownPageName = pageName;
  notePage(pageName);
  window.READLANG.pageTransitions = { goBackSwiped };
  fromHistory = false;
  animatedByApp = false;
  fromTab = false;

  // The iOS app
  const app = iOSBridge.get();
  if (!app || !(transition || appCovering)) {
    await update();
    return;
  }
  if (transition) {
    await app.beginPageTransition(transition);
  }
  await update();
  await drawn(rendered);
  app.endPageTransition();
};
